import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useIpcSyncedQuery } from '@highlight/ipc/react'

import { useAuth } from '@renderer/chat/providers'
import { useUser } from '@renderer/chat/store'
import { useIsGatewayConnected } from '@renderer/overlay/atoms'
import { getShellSessionId, isAutoModePlanAction } from '@renderer/hooks/use-long-running-action'
import { hasFeedbackHost } from '@renderer/chat/integrations/feedback-hosting'
import { isQuestionHostedByItsChat } from '@renderer/chat/integrations/question-hosting'
import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'

import { COMPLETED_ACTION_DURATION_MS } from '../staged-action-status'
import { reconcileArchiveMarks } from './archive-marks'
import { reconcileExecutionMarks } from './execution-marks'
import { getSortKey, isMockAction, NO_ACTIONS, STAGED_ACTIONS_QUERY_KEY } from './utils'
import type { StagedActionStatusGetter } from './status-hooks'
import type { StagedAction } from './types'

/**
 * Every writer that replaces rows wholesale (the list refetch, the cross-window
 * cache sync) runs the incoming rows past the state this client has already
 * confirmed, so an older read can't walk a row backwards.
 */
function reconcileConfirmedState(rows: Array<StagedAction>): Array<StagedAction> {
  return reconcileArchiveMarks(reconcileExecutionMarks(rows))
}

/**
 * Shared queryFn for the staged-actions query. Extracted so the always-mounted
 * StagedActionsProvider can register its own observer (ensuring gateway
 * invalidations trigger a real fetch even when the sidepanel is closed) without
 * duplicating the fetch logic. HIG-5126.
 */
export async function fetchStagedActions(
  getAuthToken: () => Promise<string>,
  queryClient: QueryClient,
): Promise<Array<StagedAction>> {
  const token = await getAuthToken()
  // Lean list: `view`/status/title ride the row; `?expand=true` only adds
  // conversation titles (and is hydrated on Action View open via useStagedAction).
  const response = await fetch(getChatApiRoute('api/v1/staged-actions'), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error('Failed to fetch staged actions')
  }

  const json = await response.json()
  const serverActions = json.data as Array<StagedAction>

  const currentCache = queryClient.getQueryData<Array<StagedAction>>([STAGED_ACTIONS_QUERY_KEY])
  const mockActions = currentCache?.filter((a) => isMockAction(a.id)) ?? []
  // This response may predate an invoke or archive that has already landed
  // here. Reconcile before it reaches the cache so an older read can't walk a
  // row back.
  return reconcileConfirmedState([...mockActions, ...serverActions])
}

/**
 * The single staged-actions query observer. Returns the raw (unfiltered,
 * unsorted) list. All callers share one React Query cache entry, so mounting
 * this in several places does not multiply network fetches.
 */
export function useStagedActionsQuery() {
  const { getAuthToken } = useAuth()
  const user = useUser()
  const queryClient = useQueryClient()
  const isSignedIn = !!user && !user.isAnonymous
  const isGatewayConnected = useIsGatewayConnected()

  // useIpcSyncedQuery mirrors optimistic cache writes across renderer windows via
  // GlobalEvents (no refetch), so a mutation in one window updates every window's
  // sidepanel. Broadcast only real rows and preserve each window's local `mock-`
  // placeholders, matching fetchStagedActions' mock-merge.
  const { data, isLoading, isFetching, error, refetch } = useIpcSyncedQuery({
    queryKey: [STAGED_ACTIONS_QUERY_KEY],
    queryFn: () => fetchStagedActions(getAuthToken, queryClient),
    enabled: isSignedIn,
    staleTime: 5 * 60 * 1000,
    refetchInterval: isGatewayConnected ? false : 30 * 1000,
    refetchOnWindowFocus: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    prepareBroadcast: (actions) => actions.filter((a) => !isMockAction(a.id)),
    mergeRemote: (prev, incoming) =>
      reconcileConfirmedState([...(prev ?? []).filter((a) => isMockAction(a.id)), ...incoming]),
  })

  return {
    allActions: data ?? NO_ACTIONS,
    /** True once the query has data (first fetch resolved or cache seeded). */
    hasFetched: data !== undefined,
    isLoading,
    isFetching,
    error,
    refetch,
  }
}

export function getVisibleStagedActions({
  allActions,
  manuallyHidden,
  liveShellIds,
  getActionStatus,
  archivedDisplay,
  now = Date.now(),
}: {
  allActions: Array<StagedAction>
  manuallyHidden: Set<string>
  liveShellIds: Set<string>
  getActionStatus: StagedActionStatusGetter
  /**
   * Window-local archive markers — a freshly archived row lingers for its undo
   * window. An archive from a past session simply has no entry.
   */
  archivedDisplay: Map<string, number>
  now?: number
}): Array<StagedAction> {
  const visible = allActions.filter((action) => {
    if (action.archivedAt != null) {
      // Just archived from this window (or a peer that told us): keep the row
      // for the undo window so the archive can be taken back in place. The
      // expiry sweep clearing the marker is what finally drops it.
      const archiveStart = archivedDisplay.get(action.id)
      return archiveStart != null && now - archiveStart < COMPLETED_ACTION_DURATION_MS
    }
    if (manuallyHidden.has(action.id)) return false
    const status = getActionStatus(action)
    if (status !== 'completed') return true
    // An approved auto-mode plan reads as 'completed' (it carries outputData)
    // but keeps running until its grant clears. Keep it visible while
    // unresolved — the same intent as the live-shell check below keeping a
    // coding-agent action visible while its session runs.
    if (isAutoModePlanAction(action)) {
      if (action.resolvedAt == null) return true
      // Once resolved, linger for the completion countdown so the result is
      // actually seen. ViewActionCell's expiry timer hides it (via
      // manuallyHidden) when the window elapses; this bound is what keeps
      // plans that resolved before this session from flashing back in, since
      // manuallyHidden is in-memory only. A live shell plays that role for
      // shell-backed actions below — a plan has no equivalent liveness signal.
      return now - new Date(action.resolvedAt).getTime() < COMPLETED_ACTION_DURATION_MS
    }
    const shellId = getShellSessionId(action.outputData as Record<string, unknown> | null)
    return !!shellId && liveShellIds.has(shellId)
  })

  // Rows another surface already owns. Feedback renders inside its report card;
  // a question raised by a live chat renders inline in that thread. Feedback is
  // matched against `visible`, so it stands alone again once the host goes.
  const hosted = visible.filter((action) => !hasFeedbackHost(action, visible) && !isQuestionHostedByItsChat(action))

  // Sort on precomputed keys. The previous comparator allocated two template
  // strings per comparison and ran locale-aware collation — both wasted here,
  // since the key is an ISO timestamp plus an id and is therefore pure ASCII,
  // where ordinal comparison gives the same order far more cheaply. Descending,
  // matching the original `getSortKey(b).localeCompare(getSortKey(a))`.
  return hosted
    .map((action) => ({ action, key: getSortKey(action) }))
    .sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0))
    .map((entry) => entry.action)
}
